/**
 * The reason column of the audit log — four facts, and not one of them is a blank.
 *
 * `API_CONTRACT.md` §8.10 puts the whole value of an access log in one sentence:
 * *"Dòng `data_access` mang `reason` — chính là lý do người đọc đã khai. Đây là điểm
 * khiến access log có giá trị; thiếu nó thì chỉ là một danh sách tên."* A list of
 * names with no reasons is a list of names.
 *
 * `AuditLogEntry.reason` is `string | null` on **both** kinds of row, and the `null`
 * means something different on each. Rendering it as `—`, or as an empty cell, or as
 * "không có lý do", collapses four states into one — the mistake this build has a
 * standing rule about, and the one that made `#WITHHELD_PRIVATE` necessary a layer
 * down. So this is a discriminated union over the pair, not a ternary inside a `<td>`.
 *
 *  1. **`data_access` with a reason** — the reader's own words, printed verbatim.
 *     This is the row an investigation is actually made of.
 *  2. **`data_access` with none** — the read happened and the policy did not gate it.
 *     `wp_data_access_reason_ck` refuses a `sensitive` row without a reason, so this
 *     row was **not** classified sensitive. That is a fact about the endpoint's own
 *     gating, and it is emphatically not "the reader declined to say" — which is what
 *     an empty cell in a column headed *lý do* reads as, and which would put an
 *     innocent person under suspicion in an audit report.
 *  3. **`audit` with a reason** — the reason attached to the *action*: a device
 *     revoke, a correction decision, a policy activation. Several of those make it
 *     mandatory (`wp_reg_tokens_revoke_ck` and API_CONTRACT §8.5), and it goes into
 *     `wp_audit_log.reason`. Printed verbatim too, but labelled differently, because
 *     "why I did this" and "why I looked" are different claims.
 *  4. **`audit` with none** — the action's contract does not ask for one. A fact
 *     about the operation, not about the actor.
 *
 * A reason that is present but **blank or whitespace** is folded into the "none"
 * branch of its own row type rather than printed as an empty quotation. The server's
 * `MIN_ACCESS_REASON_LENGTH` is 10 after trimming, so such a value cannot arrive
 * through the gate — but a row written by an older path, or by a job, could carry one,
 * and `""` rendered inside quotation marks is the blank cell wearing punctuation.
 */

import type { AuditLogEntry } from '../api/types';

export type AuditReason =
  | { readonly kind: 'access_reason'; readonly text: string }
  | { readonly kind: 'access_not_gated' }
  | { readonly kind: 'action_reason'; readonly text: string }
  | { readonly kind: 'action_needs_none' };

/**
 * Read the pair `(log_type, reason)` as one of four facts.
 *
 * `log_type` is read first and decides the pair of branches, because the same `null`
 * says two different things depending on which table the row came from — which is
 * exactly why a single "has a reason?" boolean would be wrong.
 */
export function reasonOf(entry: AuditLogEntry): AuditReason {
  const text = typeof entry.reason === 'string' ? entry.reason.trim() : '';
  if (entry.log_type === 'data_access') {
    return text === '' ? { kind: 'access_not_gated' } : { kind: 'access_reason', text };
  }
  return text === '' ? { kind: 'action_needs_none' } : { kind: 'action_reason', text };
}

/** Does this fact carry words the operator wrote? Never used to decide a *blank*. */
export function hasWords(reason: AuditReason): boolean {
  return reason.kind === 'access_reason' || reason.kind === 'action_reason';
}
